import React from "react";
import "./ChatHistory.css";

const ChatHistory = ({
  onAddConversation,
  conversations,
  onConversationClick,
  currentConversationIndex,
  isRequestInProgress,
  onTitleChange,
}) => {
  const [editingIndex, setEditingIndex] = React.useState(-1);
  const [editedTitle, setEditedTitle] = React.useState("");
  const titleRefs = React.useRef({});

  const startEditing = (idx) => {
    setEditingIndex(idx);
    setEditedTitle(conversations[idx]?.title || "");
    // Highlight text after state updates
    setTimeout(() => {
      const element = titleRefs.current[idx];
      if (element) {
        const range = document.createRange();
        range.selectNodeContents(element);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }, 0);
  };

  const finishEditing = (idx) => {
    const nextTitle = editedTitle ?? "";
    const currentTitle = conversations[idx]?.title ?? "";
    setEditingIndex(-1);

    if (onTitleChange && nextTitle !== currentTitle) {
      void onTitleChange(idx, nextTitle);
    }
  };

  const cancelEditing = () => {
    setEditingIndex(-1);
  };

  // if selection changes, cancel any in-progress editing
  React.useEffect(() => {
    setEditingIndex(-1);
  }, [currentConversationIndex]);

  // clear any inline backgroundColor left on history items when selection changes
  React.useEffect(() => {
    try {
      const items = document.querySelectorAll(".history-list .history-item");
      items.forEach((el) => {
        if (el && el.style && el.style.backgroundColor) {
          el.style.backgroundColor = "";
        }
      });
    } catch (e) {
      // ignore DOM errors in non-browser environments
    }
  }, [currentConversationIndex]);

  return (
    <div className="chat-history">
      <a
        href="#"
        onClick={(e) => {
          e.preventDefault();
          if (!isRequestInProgress) {
            onAddConversation();
          }
        }}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          marginTop: "6px",
          marginBottom: "16px",
          marginLeft: "2px",
          marginRight: "2px",
          padding: "8px 8px 8px 8px",
          color: "#6b7280",
          textDecoration: "none",
          cursor: isRequestInProgress ? "not-allowed" : "pointer",
          fontWeight: "500",
          fontSize: "0.85em",
          borderRadius: "4px",
          transition: "background-color 0.2s",
          opacity: isRequestInProgress ? 0.6 : 1,
          userSelect: "none",
          WebkitUserSelect: "none",
          MozUserSelect: "none",
          msUserSelect: "none",
        }}
        onMouseEnter={(e) => {
          if (!isRequestInProgress) {
            e.target.style.backgroundColor = "#f3f4f6";
          }
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = "transparent";
        }}
      >
        <span style={{ fontSize: "14px" }}>+</span>
        New Chat
      </a>

      <div className="history-list">
        {conversations.map((conversation, index) => (
          <div
            key={conversation.conversationId} // Use conversationId as key
            className={`history-item ${
              currentConversationIndex === index ? "selected" : ""
            } ${editingIndex === index ? "editing" : ""}`}
            onClick={() => {
              if (isRequestInProgress) {
                return;
              }

              if (
                currentConversationIndex === index &&
                editingIndex !== index
              ) {
                startEditing(index);
              } else if (currentConversationIndex !== index) {
                onConversationClick(index);
              }
            }}
            style={{
              fontSize: "0.85em",
              cursor: isRequestInProgress ? "not-allowed" : "pointer",
              padding: "8px 12px",
              borderRadius: "4px",
              marginBottom: "-2px", // Negative margin for overlapping effect
              transition: "background-color 0.2s",
              opacity: 1,
            }}
          >
            {editingIndex === index ? (
              <span
                ref={(el) => {
                  titleRefs.current[index] = el;
                  if (el && editingIndex === index && !el.textContent) {
                    el.textContent = editedTitle;
                  }
                }}
                contentEditable
                suppressContentEditableWarning={true}
                onInput={(e) => setEditedTitle(e.currentTarget.textContent)}
                onBlur={() => finishEditing(index)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    e.currentTarget.blur();
                  }
                  if (e.key === "Escape") {
                    e.preventDefault();
                    cancelEditing();
                  }
                }}
                style={{
                  fontSize: "1em",
                  fontWeight: "600",
                  fontFamily: "inherit",
                  lineHeight: "inherit",
                  letterSpacing: "inherit",
                  cursor: "text",
                  outline: "none",
                  border: "none",
                  padding: "0",
                  margin: "0",
                  display: "inline",
                }}
                autoFocus
              />
            ) : (
              <span
                ref={(el) => (titleRefs.current[index] = el)}
                style={{
                  cursor: isRequestInProgress ? "not-allowed" : "pointer",
                }}
              >
                {conversation.title}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ChatHistory;
